import { BaseProfileFetcher } from './base-profile-fetcher';
import { GoogleProfileFetcher } from './google-profile-fetcher';
import { GitHubProfileFetcher } from './github-profile-fetcher';
import { MicrosoftProfileFetcher } from './microsoft-profile-fetcher';
import { GenericProfileFetcher, ProfileMapping } from './generic-profile-fetcher';
import { ProviderType } from '../providers/provider.factory';
import { OAuth2Config } from '../types';

export interface ProfileFetcherOptions {
  profileUrl?: string;
  profileMapping?: ProfileMapping;
  profileHeaders?: Record<string, string>;
}

export class ProfileFetcherFactory {
  static createProfileFetcher(
    providerType: ProviderType,
    config: OAuth2Config,
    options?: ProfileFetcherOptions,
  ): BaseProfileFetcher {
    // If custom options are provided, use GenericProfileFetcher
    if (options?.profileUrl) {
      return new GenericProfileFetcher(
        options.profileUrl,
        options.profileMapping,
        options.profileHeaders,
      );
    }

    // Use provider-specific fetchers for known providers
    switch (providerType) {
      case 'google':
        return new GoogleProfileFetcher();
      case 'github':
        return new GitHubProfileFetcher();
      case 'microsoft':
      case 'outlook':
        return new MicrosoftProfileFetcher();
      case 'facebook':
        return new GenericProfileFetcher(
          'https://graph.facebook.com/me?fields=id,name,email,picture',
        );
      case 'generic':
      default:
        // For generic providers, use the profileUrl from config
        const profileUrl = config.profileUrl || config.userInfoUrl;
        if (!profileUrl) {
          throw new Error(`Profile URL must be provided for ${providerType} provider`);
        }
        return new GenericProfileFetcher(profileUrl);
    }
  }

  static registerCustomProfileFetcher(
    providerName: string,
    profileFetcher: BaseProfileFetcher,
  ): void {
    // Store custom profile fetchers for later use
    this.customFetchers.set(providerName, profileFetcher);
  }

  private static customFetchers = new Map<string, BaseProfileFetcher>();

  static getCustomProfileFetcher(providerName: string): BaseProfileFetcher | undefined {
    return this.customFetchers.get(providerName);
  }
}
